import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import { Button } from "@/components/ui/button";
import { LogOut, User, Sparkles, Undo2, Settings, HelpCircle } from "lucide-react";
import { logOut } from '@/lib/firebase';
import { Link, useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import HelpDialog from '@/components/help-dialog';

export default function Header() {
  const { user } = useAuth();
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const [helpOpen, setHelpOpen] = useState(false);

  // Toggle between the Sponsor Workbench ("new experience") and the classic
  // tools. Available to every signed-in user - unless the admin rollout flag
  // has shut the classic view off, in which case the toggle disappears.
  const { data: appSettings } = useAppSettings();
  const classicDisabled = appSettings?.disableClassicView === true;
  const inWorkbench = location.startsWith('/workbench');
  const toggleExperience = () => {
    const next = inWorkbench ? '/dashboard' : '/workbench';
    localStorage.setItem('me-experience', inWorkbench ? 'classic' : 'workbench');
    navigate(next);
  };

  const handleLogout = async () => {
    try {
      await logOut();
      // Clear any local storage or session data
      localStorage.clear();
      sessionStorage.clear();
      // Navigate to root (which shows login when not authenticated)
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout failed",
        description: "There was an issue logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo: links back to the homepage ("/" lands on the workbench or
              classic dashboard depending on the rollout toggle) */}
          <Link
            href="/"
            className="flex items-center space-x-4"
            aria-label="Go to homepage"
            data-testid="link-home"
          >
            <img
              src="https://press.marketenginuity.com/hubfs/ME%20Tailwind%20Site%202022/marketing-enginuity-logo.svg"
              alt="Marketing Enginuity"
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-gray-300"></div>
            <span className="text-lg font-semibold text-gray-900" data-testid="text-app-title">AI Sales Toolkit</span>
          </Link>

          {/* User Section */}
          {user && (
            <div className="flex items-center space-x-4">
              {/* Workbench has no sidebar, so admins get their settings entry
                  here (classic keeps the sidebar link). */}
              {user.role === 'admin' && inWorkbench && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2"
                  data-testid="button-admin-settings"
                >
                  <Settings className="w-4 h-4" />
                  Admin Settings
                </Button>
              )}
              {!classicDisabled && (
                <Button
                  variant={inWorkbench ? "secondary" : "outline"}
                  size="sm"
                  onClick={toggleExperience}
                  className={inWorkbench
                    ? "flex items-center gap-2"
                    : "flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"}
                  data-testid="button-experience-toggle"
                >
                  {inWorkbench ? <Undo2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  {inWorkbench ? "Classic view" : "New experience"}
                </Button>
              )}
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <User className="w-4 h-4" />
                <span data-testid="text-user-email">{user.email}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHelpOpen(true)}
                className="px-2.5"
                aria-label="Help and support"
                title="Help and support"
                data-testid="button-help"
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
              <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
